// Wahoo API adapter — OAuth2 PKCE browser-native authentication and ride sync.
// No backend proxy required; all token exchange happens directly in the browser.

import {
    getToken, setToken, deleteToken,
    getMeta, setMeta, getLatestRideDate, clearAllRides,
} from '../db.js';
import { setUserName } from '../userName.js';
import { reportError } from '../telemetry.js';
// Loaded from the origin root at runtime — kept external so the same bundle
// can serve every deployment with its own client ID. Namespace import (not
// named) so a stale `/config.js` missing this field fails the OAuth start
// locally instead of taking the whole bundle down with a SyntaxError at
// module instantiation.
import * as runtimeConfig from '/config.js';
const { wahooClientId: CLIENT_ID = '' } = runtimeConfig;

const AUTH_URL = 'https://api.wahooligan.com/oauth/authorize';
const TOKEN_URL = 'https://api.wahooligan.com/oauth/token';
const API_BASE = 'https://api.wahooligan.com';
const SCOPES = 'user_read workouts_read';
// Trailing slash is required: without it, some hosts 301-redirect to the
// canonical directory URL, and a TLS-terminating proxy in front of nginx
// can leak its internal scheme/port (http://host:8080/) into the Location
// header, breaking the OAuth round-trip.
const REDIRECT_PATH = '/auth/wahoo/callback/';

// Thrown when Wahoo rejects a refresh_token grant with invalid_grant — the
// refresh token itself is dead (revoked, or expired from prolonged inactivity)
// and no amount of retrying will fix it. `reauthRequired` is a plain duck-typed
// flag (rather than requiring callers to import this class) so app.js can
// react to it generically — any future OAuth adapter can set the same flag.
class WahooReauthRequiredError extends Error {
    constructor(message) {
        super(message);
        this.reauthRequired = true;
    }
}

// Wahoo workout_type_ids with family BIKING (from API docs)
const CYCLING_WORKOUT_TYPES = new Set([
    0,  // BIKING
    11, // BIKING_CYCLECROSS
    12, // BIKING_INDOOR
    13, // BIKING_MOUNTAIN
    14, // BIKING_RECUMBENT
    15, // BIKING_ROAD
    16, // BIKING_TRACK
    17, // BIKING_MOTOCYCLING
    49, // BIKING_INDOOR_CYCLING_CLASS
    61, // BIKING_INDOOR_TRAINER
    64, // EBIKING
    68, // BIKING_INDOOR_VIRTUAL
    70, // HANDCYCLING
]);

// ── PKCE helpers ──────────────────────────────────────────────────────────────

function _base64url(buffer) {
    return btoa(String.fromCharCode(...buffer))
        .replace(/\+/g, '-')
        .replace(/\//g, '_')
        .replace(/=/g, '');
}

async function _generateCodeVerifier() {
    const array = new Uint8Array(96);
    crypto.getRandomValues(array);
    return _base64url(array);
}

async function _generateCodeChallenge(verifier) {
    const data = new TextEncoder().encode(verifier);
    const digest = await crypto.subtle.digest('SHA-256', data);
    return _base64url(new Uint8Array(digest));
}

function _redirectUri() {
    return window.location.origin + REDIRECT_PATH;
}

// ── Adapter ───────────────────────────────────────────────────────────────────

const wahooAdapter = {
    id: 'wahoo',
    name: 'Wahoo',
    kind: 'sync',
    description: 'Sync rides directly from your Wahoo account via OAuth',
    infoText: "Sync rides directly from your Wahoo account. After account is connected, new rides are fetched automatically each time you open the app. Only new rides are added.",

    async renderConfig(container, onComplete, onDisconnect) {
        this._onComplete = onComplete;
        const token = await getToken('wahoo');

        if (!token) {
            const reauthRequired = await getMeta('wahooReauthRequired');
            container.innerHTML = `
                <button id="btn-wahoo-connect" class="btn-brut btn-brut--primary">
                    Connect with Wahoo
                </button>
                ${reauthRequired ? '<p class="hint hint--error">Your Wahoo session expired. Reconnect to keep syncing.</p>' : ''}
                <p class="hint">
                    You'll be redirected to Wahoo to sign in and authorize read-only
                    access to your ride history. Data is only stored locally in your browser.
                </p>
                <div class="adapter-status" aria-live="polite"></div>
            `;
            container.querySelector('#btn-wahoo-connect')
                .addEventListener('click', () => this.startAuth());
        } else {
            const lastOp = await getMeta('lastOp_wahoo');
            const statusText = lastOp
                ? `${lastOp.message} · ${new Date(lastOp.timestamp).toLocaleString()}`
                : 'Not yet synced';
            const disconnectLabel = token.user_name
                ? `Disconnect user ${token.user_name}`
                : 'Disconnect';
            // Connected sources that are not the active one read "Activate" — the
            // sync also makes them active (ADR-0005, D1).
            const isActiveSource = (await getMeta('activeAdapter')) === 'wahoo';
            container.innerHTML = `
                <button id="btn-wahoo-sync" class="btn-brut btn-brut--primary adapter-action-sync">
                    ${isActiveSource ? 'Sync now' : 'Activate'}
                </button>
                <button id="btn-wahoo-disconnect" class="btn-brut">
                    ${disconnectLabel}
                </button>
                <div class="adapter-status" aria-live="polite">${statusText}</div>
            `;
            const statusEl = container.querySelector('.adapter-status');
            container.querySelector('#btn-wahoo-sync')
                .addEventListener('click', async () => {
                    try {
                        statusEl.textContent = 'Syncing...';
                        // Demo rides are cleared when a real source loads (ADR-0005
                        // exclusivity), so they must not advance the incremental-sync
                        // bookmark — activating from demo syncs the full history.
                        const onDemo = (await getMeta('activeAdapter')) === 'demo-data';
                        const latestDate = onDemo ? null : await getLatestRideDate();
                        const result = await this.sync(msg => { statusEl.textContent = msg; }, latestDate);
                        onComplete(result);
                    } catch (e) {
                        reportError(e, { adapter: 'wahoo', op: 'sync-now' });
                        if (e instanceof WahooReauthRequiredError) {
                            container.innerHTML = '';
                            this.renderConfig(container, onComplete, onDisconnect);
                            return;
                        }
                        statusEl.textContent = `Sync failed: ${e.message}`;
                    }
                });
            container.querySelector('#btn-wahoo-disconnect')
                .addEventListener('click', () => onDisconnect?.());
        }
    },

    async startAuth() {
        const verifier = await _generateCodeVerifier();
        const challenge = await _generateCodeChallenge(verifier);
        const state = _base64url(crypto.getRandomValues(new Uint8Array(16)));

        // Store verifier in IndexedDB — persists across sessions, required for token refresh
        await setToken({ key: 'wahoo-pkce-verifier', value: verifier });
        // Store state in sessionStorage — CSRF check only, intentionally short-lived
        sessionStorage.setItem('wahoo-oauth-state', state);

        const params = new URLSearchParams({
            client_id: CLIENT_ID,
            redirect_uri: _redirectUri(),
            response_type: 'code',
            scope: SCOPES,
            code_challenge: challenge,
            code_challenge_method: 'S256',
            state
        });
        location.href = `${AUTH_URL}?${params}`;
    },

    // Entry point for `static/auth/wahoo/callback/index.html`. Parses the
    // OAuth redirect, runs the token exchange, and orchestrates the
    // post-connect app state before bouncing to the dashboard.
    async handleCallbackPage() {
        const params = new URL(location.href).searchParams;
        const code = params.get('code');
        const state = params.get('state');
        const error = params.get('error');

        if (error || !code) {
            this._showCallbackError(`Authorization failed: ${error || 'no code received'}.`);
            return;
        }

        try {
            await this.handleCallback(code, state);
            // Demo is exclusive (ADR-0005): connecting a real source clears the
            // demo rides. Checked by active-source id before we repoint it below.
            if ((await getMeta('activeAdapter')) === 'demo-data') {
                await clearAllRides();
            }
            const token = await getToken('wahoo');
            if (token?.user_name) await setUserName(token.user_name);
            // The dashboard autosyncs the active adapter on load (when its kind is
            // sync) — setting it here triggers the first sync after a fresh
            // connect, without needing a one-shot URL marker.
            await setMeta('activeAdapter', 'wahoo');
            location.replace('/');
        } catch (e) {
            reportError(e, { adapter: 'wahoo', op: 'oauth-callback' });
            this._showCallbackError(e.message || 'Unknown error during token exchange.');
        }
    },

    _showCallbackError(msg) {
        const status = document.getElementById('status');
        if (!status) return;
        status.replaceChildren();
        const p = document.createElement('p');
        p.className = 'error';
        p.textContent = msg;
        const a = document.createElement('a');
        a.href = '/';
        a.textContent = 'Back to dashboard';
        status.append(p, a);
    },

    async handleCallback(code, state) {
        const expectedState = sessionStorage.getItem('wahoo-oauth-state');
        if (state !== expectedState) {
            throw new Error('OAuth state mismatch — possible CSRF attack');
        }
        sessionStorage.removeItem('wahoo-oauth-state');

        const verifierEntry = await getToken('wahoo-pkce-verifier');
        if (!verifierEntry) throw new Error('PKCE verifier not found');

        const resp = await fetch(TOKEN_URL, {
            method: 'POST',
            headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
            body: new URLSearchParams({
                grant_type: 'authorization_code',
                client_id: CLIENT_ID,
                redirect_uri: _redirectUri(),
                code,
                code_verifier: verifierEntry.value
            })
        });
        if (!resp.ok) throw new Error(`Token exchange failed: ${resp.status}`);
        const data = await resp.json();

        // Fetch the user's display name
        let userName = '';
        try {
            const userResp = await fetch(`${API_BASE}/v1/user`, {
                headers: { Authorization: `Bearer ${data.access_token}` }
            });
            if (userResp.ok) {
                const user = await userResp.json();
                userName = [user.first, user.last].filter(Boolean).join(' ');
            }
        } catch { /* user name is optional */ }

        await setToken({
            key: 'wahoo',
            access_token: data.access_token,
            refresh_token: data.refresh_token,
            expires_at: Date.now() + data.expires_in * 1000,
            user_name: userName
        });
        await setMeta('wahooReauthRequired', null);

        // Remove OAuth params from the URL without triggering a reload
        history.replaceState(null, '', location.pathname);
    },

    async refreshTokenIfNeeded() {
        const token = await getToken('wahoo');
        if (!token) throw new Error('Not authenticated with Wahoo');

        // Still valid for more than 5 minutes
        if (Date.now() < token.expires_at - 5 * 60 * 1000) return token;

        const verifierEntry = await getToken('wahoo-pkce-verifier');
        if (!verifierEntry) throw new Error('PKCE verifier not found for token refresh');

        const resp = await fetch(TOKEN_URL, {
            method: 'POST',
            headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
            body: new URLSearchParams({
                grant_type: 'refresh_token',
                client_id: CLIENT_ID,
                refresh_token: token.refresh_token,
                code_verifier: verifierEntry.value
            })
        });
        if (!resp.ok) {
            const errBody = await resp.json().catch(() => null);
            if (errBody?.error === 'invalid_grant') {
                await this._clearExpiredSession();
                throw new WahooReauthRequiredError('Wahoo session expired — please reconnect.');
            }
            throw new Error(`Token refresh failed: ${resp.status}`);
        }
        const data = await resp.json();

        const updated = {
            ...token,
            access_token: data.access_token,
            expires_at: Date.now() + data.expires_in * 1000,
            ...(data.refresh_token && { refresh_token: data.refresh_token })
        };
        await setToken(updated);
        return updated;
    },

    // A dead refresh token can't be recovered client-side — drop the stored
    // credentials so the UI falls back to the "Connect" state, and flag
    // wahooReauthRequired so renderConfig can explain why (cleared again on
    // the next successful connect, in handleCallback).
    async _clearExpiredSession() {
        await deleteToken('wahoo');
        await deleteToken('wahoo-pkce-verifier');
        if ((await getMeta('activeAdapter')) === 'wahoo') await setMeta('activeAdapter', null);
        await setMeta('wahooReauthRequired', true);
    },

    async getUserName() {
        const token = await getToken('wahoo');
        return token?.user_name || '';
    },

    async revokeAccess() {
        const token = await getToken('wahoo');
        if (token) {
            try {
                await fetch(`${API_BASE}/v1/permissions`, {
                    method: 'DELETE',
                    headers: { Authorization: `Bearer ${token.access_token}` }
                });
            } catch { /* ignore errors during revocation */ }
        }
        await deleteToken('wahoo');
        await deleteToken('wahoo-pkce-verifier');
    },

    async sync(progressCb, latestDate) {
        const token = await this.refreshTokenIfNeeded();

        progressCb?.('Fetching workouts from Wahoo...');
        const workouts = await this._fetchWorkouts(token.access_token, progressCb, latestDate);

        const rides = workouts
            .filter(w => CYCLING_WORKOUT_TYPES.has(w.workout_type_id))
            .map(w => this._mapToRide(w));

        return {
            rides,
            message: rides.length > 0
                ? `Synced ${rides.length} new ride${rides.length === 1 ? '' : 's'} from Wahoo.`
                : 'Up to date.'
        };
    },

    // Wahoo's /v1/workouts endpoint has no server-side date filter and always
    // returns workouts sorted by `starts` DESC. To do an incremental sync we
    // paginate newest-first and stop once we see a workout at or before the
    // latest date already in the DB.
    async _fetchWorkouts(accessToken, progressCb, latestDate = null) {
        const all = [];
        let page = 1;
        while (true) {
            progressCb?.(`Fetching page ${page}...`);
            const params = new URLSearchParams({ per_page: '50', page: String(page) });
            const resp = await fetch(`${API_BASE}/v1/workouts?${params}`, {
                headers: { Authorization: `Bearer ${accessToken}` },
                cache: 'no-store'
            });
            if (resp.status === 429) throw new Error('Rate limited — please try again later.');
            if (!resp.ok) throw new Error(`Wahoo API error: ${resp.status}`);
            const data = await resp.json();
            if (!data.workouts?.length) break;

            let reachedOld = false;
            for (const w of data.workouts) {
                // Both sides are ISO 8601; lexicographic compare works whether
                // latestDate is date-only or a full timestamp.
                if (latestDate && w.starts < latestDate) {
                    reachedOld = true;
                    break;
                }
                all.push(w);
            }
            if (reachedOld) break;
            if (all.length >= data.total) break;
            page++;
        }
        return all;
    },

    _mapToRide(w) {
        const s = w.workout_summary ?? {};
        return {
            id: String(w.id),
            source: 'wahoo',
            title: w.name || 'Wahoo Ride',
            date: w.starts,
            distance_km: parseFloat((s.distance_accum / 1000).toFixed(2)) || 0,
            speed_kmh: parseFloat(((s.speed_avg || 0) * 3.6).toFixed(2)),
            duration_seconds: parseFloat(s.duration_active_accum) || 0,
            user_id: String(w.user_id || '')
        };
    },

    cleanup() {}
};

export default wahooAdapter;
